import type { ApprovalAction } from '@didto/domain';
import { formatInTimeZone, formatMinor, isToSettle, refundableMinor } from '@didto/domain';
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Loading,
  PageHeader,
  Pills,
  SectionHead,
  Sheet,
  StatusChip,
} from '@didto/ui';
import type { ShapeKind } from '@didto/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { KeyboardEvent } from 'react';
import { useState } from 'react';
import { useDirectActions } from '@/app/directActions';
import { useSession } from '@/app/session';
import { useToast } from '@/app/toast';
import { useSetting } from '@/app/useSetting';
import type { Row } from '@/lib/db';
import { errorText } from '@/lib/errors';
import type { AppError } from '@/lib/errors';
import { useOnline } from '@/lib/network';
import {
  FULFILMENT_LABEL,
  ORDER_TYPE_LABEL,
  fetchOrderDetail,
  fetchRecentOrders,
  hintOf,
  latestOrderVersion,
  orderKeys,
} from '@/lib/orders';
import type { Order } from '@/lib/orders';
import { fetchOrderMoney, fetchPaymentMethods, paymentKeys } from '@/lib/payments';
import type { Payment, Receipt } from '@/lib/payments';
import { topicPos, useRealtimeInvalidate } from '@/lib/realtime';
import { ORDER_STATUS_TONE, PAYMENT_STATUS_TONE, fulfilmentTone, ticketTone } from '@/lib/status';
import { supabase } from '@/lib/supabase';
import { CorrectServeButton, serveCorrection } from '../expo/serveCorrection';
import {
  allowanceNote,
  approvalCopy,
  approvalRequest,
  voidApprovalPermission,
} from '../pos/approvals';
import { TerminalPicker, moneyAtPayload, useCashMovesNow } from '../pos/moneyAt';
import type { RefundInput } from '../pos/paymentSheets';
import { MoneyPanel, RefundSheet, SlipSheet } from '../pos/paymentSheets';
import { GiveBackSheet, GiveBacksPanel, reversalIdsOf } from '../pos/giveBacks';
import { WriteOffsPanel, useOrderWriteOffs, usePickerEmployees } from '../pos/writeOffs';
import { ReturnsPanel, useOrderReturns } from '../pos/returns';
import { usePosContext, useRun, useTables } from '../pos/posData';
import type { Approval, ApprovalRequest, ReasonRequest } from '../pos/sheets';
import { ApprovalSheet, LineBadge, ReasonSheet } from '../pos/sheets';
import { OutsideShiftRefundSheet } from './OutsideShiftRefund';
import './orders.css';

type Filter = 'open' | 'all';
const FILTERS: { value: Filter; label: string }[] = [
  { value: 'open', label: 'Open' },
  { value: 'all', label: 'Recent' },
];
const SERVE_INVALIDATE: readonly (readonly string[])[] = [['orders'], ['expo'], ['table-sessions']];
type Station = Pick<Row<'production_station'>, 'id' | 'name'>;

const clockTime = (iso: string, timeZone: string) =>
  new Intl.DateTimeFormat('en-PH', { timeZone, hour: 'numeric', minute: '2-digit' }).format(
    new Date(iso),
  );

/**
 * The timeline in words next to each event's name (the name stays as recorded: it is what the
 * audit and the support tools search for). An event without a label shows its name only.
 */
const EVENT_LABEL: Partial<Record<string, string>> = {
  'order.placed': 'Order placed',
  'order.recovered': 'Entered from paper (taken offline)',
  'order.updated': 'Order details changed',
  'order.line_added': 'Line added',
  'order.line_updated': 'Line changed',
  'order.line_removed': 'Line removed',
  'order.line_voided': 'Line voided',
  'order.line_returned': 'Line returned',
  'order.sent': 'Sent to the kitchen and bar',
  'order.auto_send_failed': 'Automatic send failed',
  'order.cancelled': 'Order cancelled',
  'order.voided': 'Order voided',
  'order.completed': 'Order completed',
  'order.reopened': 'Order reopened',
  'order.written_off': 'Balance written off',
  'order.write_off_reinstated': 'Write-off taken back',
  'order.in_progress': 'Preparation started',
  'order.partially_ready': 'Partly ready',
  'order.ready': 'All ready',
  'order.partially_served': 'Partly served',
  'order.served': 'All served',
  'order.seated': 'Seated at a table',
  'order.unseated': 'Moved off the table',
  'order.table_cleared': 'Table cleared',
  'payment.captured': 'Payment taken',
  'payment.voided': 'Payment voided',
  'refund.created': 'Money given back',
  'refund.cancelled': 'Refund cancelled',
  'receipt.issued': 'Slip issued',
  'ticket.created': 'Ticket sent to a station',
  'ticket.cancelled': 'Ticket cancelled',
  'ticket.started': 'Ticket started',
  'ticket.ready': 'Ticket ready',
  'ticket.undone': 'Ticket back in preparation',
  'ticket.recalled': 'Ticket recalled',
  'ticket.dismissed': 'Cancelled ticket dismissed',
  'ticket.served': 'Ticket served',
  'ticket.unserved': 'Serve taken back',
  'item.started': 'Item started',
  'item.ready': 'Item ready',
  'item.undone': 'Item back in preparation',
  'item.recalled': 'Item recalled',
};

/** Open on Enter or Space, like a button (the row is the list's way into an order). */
const onRowKey = (open: () => void) => (e: KeyboardEvent<HTMLTableRowElement>) => {
  if (e.key !== 'Enter' && e.key !== ' ') return;
  e.preventDefault();
  open();
};

/** The order's own state leads, with its shape: ○ open, ● completed, ■ cancelled or voided. */
const ORDER_STATUS_SHAPE: Record<Order['status'], ShapeKind> = {
  open: 'ring',
  completed: 'dot',
  cancelled: 'square',
  voided: 'square',
};

/** An order's state, its payment and its kitchen progress, in words (tones: lib/status.ts): the
 * state first and strongest, the payment and the kitchen after it, quieter. */
function OrderStates({ o }: { o: Pick<Order, 'status' | 'payment_status' | 'fulfilment_status'> }) {
  return (
    <>
      <StatusChip tone={ORDER_STATUS_TONE[o.status]} shape={ORDER_STATUS_SHAPE[o.status]}>
        {o.status}
      </StatusChip>
      <Badge tone={PAYMENT_STATUS_TONE[o.payment_status]}>
        {o.payment_status.replace('_', ' ')}
      </Badge>
      <Badge tone={fulfilmentTone(o.fulfilment_status)}>
        {FULFILMENT_LABEL[o.fulfilment_status]}
      </Badge>
    </>
  );
}

/** Orders at the active location: today's queue for managers, with the full timeline per order. */
export function OrdersPage() {
  const s = useSession();
  const ctx = usePosContext();
  const { toast } = useToast();
  const online = useOnline();
  const run = useRun(ctx);
  const qc = useQueryClient();
  const tables = useTables(ctx.locationId);
  const [filter, setFilter] = useState<Filter>('open');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [approval, setApproval] = useState<ApprovalRequest | null>(null);
  const [reason, setReason] = useState<ReasonRequest | null>(null);
  const [refunding, setRefunding] = useState(false);
  // ADR-033: the owner's refund by GCash or transfer, outside any shift
  const [refundingOutside, setRefundingOutside] = useState(false);
  const [slip, setSlip] = useState<Receipt | null>(null);
  // ADR-029: after money went back by GCash or a bank transfer, its reference (and a void's answer)
  const [giveBack, setGiveBack] = useState<{ orderId: string; reversalIds: string[] } | null>(null);
  const [busy, setBusy] = useState(false);

  const orders = useQuery({
    queryKey: orderKeys.recent(ctx.locationId),
    enabled: !!ctx.locationId,
    queryFn: () => fetchRecentOrders(ctx.locationId),
    refetchInterval: 20_000,
  });
  const detail = useQuery({
    queryKey: orderKeys.detail(selectedId ?? ''),
    enabled: !!selectedId,
    queryFn: () => fetchOrderDetail(selectedId ?? ''),
    refetchInterval: 20_000,
  });
  useRealtimeInvalidate(ctx.locationId ? topicPos(ctx.locationId) : null, [['orders']]);
  const money = useQuery({
    queryKey: paymentKeys.money(selectedId ?? ''),
    enabled: !!selectedId,
    queryFn: () => fetchOrderMoney(selectedId ?? ''),
  });
  const methods = useQuery({
    queryKey: paymentKeys.methods,
    queryFn: fetchPaymentMethods,
    staleTime: 60_000,
  });
  const writeOffs = useOrderWriteOffs(selectedId);
  const returns = useOrderReturns(
    selectedId,
    (detail.data?.items ?? []).map((i) => i.id),
  );
  const people = usePickerEmployees(!!selectedId);
  const employeeName = (id: string | null) =>
    (people.data ?? []).find((e) => e.id === id)?.display_name ?? 'someone';
  const stations = useQuery({
    queryKey: ['stations', 'names', ctx.locationId],
    enabled: !!ctx.locationId,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('production_station')
        .select('id, name')
        .eq('location_id', ctx.locationId);
      if (error) throw error;
      return data as Station[];
    },
  });

  const tableName = (id: string | null) =>
    (tables.data ?? []).find((t) => t.id === id)?.name ?? null;
  const stationName = (id: string) =>
    (stations.data ?? []).find((x) => x.id === id)?.name ?? 'station';
  const list = (orders.data ?? []).filter((o) => filter === 'all' || o.status === 'open');
  const sel = selectedId ? (detail.data ?? null) : null;
  const isDevice = ctx.principal === 'device';
  // ADR-024: money given back goes through a drawer (the owner in a browser names its tablet)
  const moneyAt = moneyAtPayload(ctx);
  const cashNow = useCashMovesNow();

  /** ADR-023: a refused command asks for an approval of exactly what it was about to send. */
  const approvalFor = (
    kind: ApprovalAction,
    payload: Record<string, unknown>,
    permission: string,
    e: AppError,
    retry: (a?: Approval) => Promise<void>,
  ) => {
    // only a refusal that names this approval opens the sheet (not "another cashier's shift")
    if (e.code !== 'forbidden' || !isDevice || hintOf(e).requires !== permission) return false;
    setApproval(approvalRequest(kind, payload, approvalCopy(permission, e), retry));
    return true;
  };
  const cancel = () =>
    setReason({
      title: 'Cancel order',
      label: 'Reason',
      required: false,
      confirmLabel: 'Cancel order',
      danger: true,
      onConfirm: async (why) => {
        if (!sel) return;
        const r = await run('order.cancel', {
          order_id: sel.order.id,
          version: sel.order.version,
          ...(why ? { reason: why } : {}),
        });
        if (r.ok) toast(`Order ${sel.order.order_number} cancelled`);
      },
    });
  const doVoid = async (why: string, a?: Approval, cashMovesNow = false) => {
    if (!sel) return;
    const payload = {
      order_id: sel.order.id,
      version: sel.order.version,
      reason: why,
      ...moneyAt,
      ...(cashMovesNow ? { cash_moves_now: true } : {}),
    };
    const r = await run(
      'order.void',
      { ...payload, ...(a ?? {}) },
      {
        handle: (e) =>
          cashNow.handle(e, () => doVoid(why, a, true)) ||
          approvalFor('order.void', payload, voidApprovalPermission(e), e, (ap) =>
            doVoid(why, ap, cashMovesNow),
          ),
      },
    );
    if (r.ok) {
      toast(`Order ${sel.order.order_number} voided`);
      setGiveBack({ orderId: sel.order.id, reversalIds: reversalIdsOf(r.result) });
    }
  };
  const voidOrder = () =>
    setReason({
      title: 'Void order',
      label: 'Reason',
      required: true,
      confirmLabel: 'Void order',
      danger: true,
      onConfirm: (why) => doVoid(why),
    });
  const voidPayment = (p: Payment, reversibleMinor: number) => {
    if (!sel) return;
    const opened = { id: sel.order.id, version: sel.order.version };
    setReason({
      // a void reverses what is left of the tender: its amount minus what was refunded from it
      title: `Void ${p.method_name_snapshot} ${formatMinor(reversibleMinor, ctx.currency)}`,
      label: 'Reason',
      required: true,
      confirmLabel: 'Void payment',
      danger: true,
      onConfirm: (why) => doVoidPayment(opened, p, why),
    });
  };
  const doVoidPayment = async (
    opened: { id: string; version: number },
    p: Payment,
    why: string,
    a?: Approval,
    cashMovesNow = false,
  ) => {
    const payload = {
      order_id: opened.id,
      // the order as the screen last received it, read now: not as it was when the sheet opened
      version: latestOrderVersion(qc, opened.id, opened.version),
      payment_id: p.id,
      reason: why,
      ...moneyAt,
      ...(cashMovesNow ? { cash_moves_now: true } : {}),
    };
    const r = await run(
      'payment.void',
      { ...payload, ...(a ?? {}) },
      {
        handle: (e) =>
          cashNow.handle(e, () => doVoidPayment(opened, p, why, a, true)) ||
          approvalFor('payment.void', payload, 'refund.approve', e, (ap) =>
            doVoidPayment(opened, p, why, ap, cashMovesNow),
          ),
      },
    );
    if (r.ok) {
      const reversedMinor = Number(r.result.reversed_minor ?? 0);
      toast(
        `Payment voided; ${formatMinor(reversedMinor, ctx.currency)} reversed${allowanceNote(r.result, reversedMinor, ctx.currency)}`,
      );
      setGiveBack({ orderId: opened.id, reversalIds: reversalIdsOf(r.result) });
    }
  };
  const refund = async (input: RefundInput, a?: Approval, cashMovesNow = false) => {
    if (!sel) return;
    setBusy(true);
    const payload = {
      order_id: sel.order.id,
      version: sel.order.version,
      ...input,
      ...moneyAt,
      ...(cashMovesNow ? { cash_moves_now: true } : {}),
    };
    const r = await run(
      'order.refund',
      { ...payload, ...(a ?? {}) },
      {
        handle: (e) =>
          cashNow.handle(e, () => refund(input, a, true)) ||
          approvalFor('order.refund', payload, 'refund.approve', e, (ap) =>
            refund(input, ap, cashMovesNow),
          ),
      },
    );
    setBusy(false);
    if (r.ok) {
      setRefunding(false);
      toast(
        `Refunded ${formatMinor(input.amount_minor, ctx.currency)}${allowanceNote(r.result, input.amount_minor, ctx.currency)}`,
        'success',
      );
      setGiveBack({ orderId: sel.order.id, reversalIds: reversalIdsOf(r.result) });
    }
  };

  /* ── serve corrections (ADR-022): the cashier or the owner directly, a server with approval ── */
  const serveActions = useDirectActions(
    ctx.principal === 'human' ? ctx.locationId : null,
    SERVE_INVALIDATE,
  );
  const undoWindow = Number(useSetting('serve.undo_window_seconds'));
  const canServeHere = s.can('serve.operate') && (isDevice || !!ctx.terminal);
  const correctServe = serveCorrection({
    run: serveActions.run,
    toast,
    setReason,
    setApproval,
    terminalPayload: !isDevice && ctx.terminal ? { terminal_id: ctx.terminal.id } : {},
    isDevice,
    canCorrect: s.can('serve.correct'),
    undoWindow,
  });

  const canEdit = s.can('order.create');
  const canVoid = s.can('order.void') || isDevice;
  const canRefund = s.can('refund.perform') || isDevice;
  const canRefundOutside = ctx.principal === 'human' && s.can('refund.outside_shift');
  const canVoidPayment = s.can('payment.void') || isDevice;

  /* ── what the order sheet offers (each needs the server now: it waits for the connection) ── */
  const live = sel ? sel.order.status === 'open' || sel.order.status === 'completed' : false;
  const refundable = sel
    ? refundableMinor({ paidMinor: sel.order.paid_minor, refundedMinor: sel.order.refunded_minor })
    : 0;
  const offerRefund = live && refundable > 0 && canRefund;
  const offerRefundOutside = live && refundable > 0 && canRefundOutside;
  const offerCancel =
    !!sel &&
    sel.order.status === 'open' &&
    sel.order.sent_at === null &&
    sel.order.paid_minor === 0 &&
    canEdit;
  const offerVoid =
    !!sel &&
    sel.order.status === 'open' &&
    (sel.order.sent_at !== null || sel.order.paid_minor > 0) &&
    canVoid;

  return (
    <div className="d-page">
      <PageHeader
        eyebrow="Operations"
        title="Orders"
        subtitle="Open orders and recent history for this location, with payments and reversals."
        actions={
          <>
            <TerminalPicker ctx={ctx} />
            <Pills value={filter} options={FILTERS} onChange={setFilter} ariaLabel="Filter" />
          </>
        }
      />
      {orders.error && (
        <ErrorState
          title={orders.data ? 'Could not refresh the orders' : 'Could not load the orders'}
          message={errorText(orders.error)}
          onRetry={() => void orders.refetch()}
        />
      )}
      {orders.isLoading && <Loading rows={5} label="Loading the orders" />}
      {orders.data && list.length === 0 && (
        <EmptyState title={filter === 'open' ? 'No open orders' : 'No orders yet'} />
      )}
      {list.length > 0 && (
        <div className="d-table-scroll orders-table">
          <table className="d-table" data-testid="orders-list">
            <thead>
              <tr>
                <th scope="col">Number</th>
                <th scope="col">Placed</th>
                <th scope="col">Type</th>
                <th scope="col">Status</th>
                <th scope="col" className="d-num">
                  Total
                </th>
              </tr>
            </thead>
            <tbody>
              {list.map((o) => (
                <tr
                  key={o.id}
                  className="d-table__row--button"
                  tabIndex={0}
                  onClick={() => setSelectedId(o.id)}
                  onKeyDown={onRowKey(() => setSelectedId(o.id))}
                  data-testid={`order-row-${o.order_number}`}
                >
                  <td className="d-mono orders-table__number">{o.order_number}</td>
                  <td className="orders-table__placed">
                    {formatInTimeZone(o.placed_at, ctx.timeZone)}
                  </td>
                  <td className="orders-table__type">
                    {ORDER_TYPE_LABEL[o.type]}
                    {o.dining_table_id ? ` · ${tableName(o.dining_table_id) ?? 'table'}` : ''}
                    {o.customer_name ? ` · ${o.customer_name}` : ''}
                  </td>
                  <td className="orders-table__status">
                    <span className="d-inline">
                      <OrderStates o={o} />
                    </span>
                  </td>
                  <td className="d-num d-mono orders-table__total">
                    {formatMinor(o.total_minor, ctx.currency)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Sheet
        open={!!selectedId}
        onClose={() => setSelectedId(null)}
        title={sel ? `Order ${sel.order.order_number}` : 'Order'}
      >
        {detail.isLoading && <Loading label="Loading the order…" />}
        {detail.error && !sel && (
          <ErrorState
            title="Could not load this order"
            message={errorText(detail.error)}
            onRetry={() => void detail.refetch()}
          />
        )}
        {sel && (
          <div className="d-stack" data-testid="order-detail">
            <div className="d-stack d-stack--tight">
              <div className="d-inline">
                <Badge tone="accent">{ORDER_TYPE_LABEL[sel.order.type]}</Badge>
                {sel.order.dining_table_id && (
                  <Badge>{tableName(sel.order.dining_table_id) ?? 'table'}</Badge>
                )}
                {sel.order.customer_name && <Badge>{sel.order.customer_name}</Badge>}
                {sel.order.scheduled_for && (
                  <Badge tone="info">
                    Ready {formatInTimeZone(sel.order.scheduled_for, ctx.timeZone)}
                  </Badge>
                )}
              </div>
              <div className="d-inline">
                <OrderStates o={sel.order} />
                {isToSettle(sel.order) && <Badge tone="warning">TO SETTLE</Badge>}
              </div>
              {(sel.order.cancel_reason ?? sel.order.void_reason) && (
                <p className="d-muted d-break">
                  Reason: {sel.order.cancel_reason ?? sel.order.void_reason}
                </p>
              )}
            </div>

            <section className="d-stack d-stack--tight">
              <SectionHead level={3} title="Lines" />
              <div>
                {sel.items.map((i) => {
                  const mods = sel.modifiers.filter((m) => m.order_item_id === i.id);
                  const returned = returns.data?.returned[i.id] ?? 0;
                  const meta = [
                    i.variant_name_snapshot,
                    ...mods.map((m) => m.option_name_snapshot),
                    i.notes ? `“${i.notes}”` : null,
                    returned > 0 ? `${returned} returned` : null,
                  ]
                    .filter(Boolean)
                    .join(' · ');
                  return (
                    <div
                      className={`d-cartline${i.status === 'voided' ? ' d-cartline--void' : ''}`}
                      key={i.id}
                    >
                      <div className="d-cartline__main">
                        <div className="d-row__title">
                          {i.qty} × {i.name_snapshot}
                        </div>
                        {meta && <div className="d-row__meta">{meta}</div>}
                        <div className="d-inline orders-line__tags">
                          <LineBadge status={i.status} sentAt={i.sent_at} />
                          {i.production_route_code_snapshot && (
                            <span className="d-faint d-mono">
                              {i.production_route_code_snapshot}
                            </span>
                          )}
                          {i.void_reason && (
                            <span className="d-faint d-break">{i.void_reason}</span>
                          )}
                        </div>
                      </div>
                      <strong className="d-mono orders-money">
                        {formatMinor(i.line_total_minor, ctx.currency)}
                      </strong>
                    </div>
                  );
                })}
              </div>
              <div className="d-totals">
                <span>Total</span>
                <strong className="d-mono">
                  {formatMinor(sel.order.total_minor, ctx.currency)}
                </strong>
              </div>
              {(sel.order.paid_minor > 0 ||
                sel.order.refunded_minor > 0 ||
                sel.order.written_off_minor > 0) && (
                <p className="d-muted d-num" data-testid="orders-money-summary">
                  Paid {formatMinor(sel.order.paid_minor, ctx.currency)}
                  {sel.order.refunded_minor > 0
                    ? ` · refunded ${formatMinor(sel.order.refunded_minor, ctx.currency)}`
                    : ''}
                  {sel.order.written_off_minor > 0
                    ? ` · written off ${formatMinor(sel.order.written_off_minor, ctx.currency)}`
                    : ''}
                </p>
              )}
            </section>
            <MoneyPanel
              money={money.data}
              currency={ctx.currency}
              timeZone={ctx.timeZone}
              canVoid={live ? canVoidPayment : false}
              onVoid={voidPayment}
              onSlip={setSlip}
            />
            {/* ADR-031: write-offs are recorded on the tablet; listed here as they stand */}
            <WriteOffsPanel
              order={sel.order}
              rows={writeOffs.data ?? []}
              currency={ctx.currency}
              canWriteOff={false}
              nameOf={employeeName}
              onStart={() => undefined}
              onReinstate={() => undefined}
            />
            {/* ADR-032: returns are recorded on the tablet; listed here */}
            <ReturnsPanel
              rows={returns.data?.rows ?? []}
              items={sel.items}
              currency={ctx.currency}
              nameOf={employeeName}
            />
            <GiveBacksPanel orderId={sel.order.id} currency={ctx.currency} run={run} />
            {sel.tickets.length > 0 && (
              <section className="d-stack d-stack--tight">
                <SectionHead level={3} title="Production tickets" />
                {!canServeHere &&
                  s.can('serve.operate') &&
                  sel.tickets.some((t) => t.status === 'served') && (
                    <p className="d-help">
                      Choose a terminal on the POS or Expo page to correct a serve from this
                      browser.
                    </p>
                  )}
                <div className="d-list" data-testid="order-tickets">
                  {sel.tickets.map((t) => (
                    <div className="d-row" key={t.id}>
                      <div className="d-row__main">
                        <div className="d-row__title">
                          #{t.ticket_seq} · {stationName(t.production_station_id)}
                          {t.is_addition ? ' · addition' : ''}
                        </div>
                        <div className="d-row__meta">
                          sent {formatInTimeZone(t.sent_at, ctx.timeZone)}
                          {t.started_at
                            ? ` · started ${clockTime(t.started_at, ctx.timeZone)}`
                            : ''}
                          {t.ready_at ? ` · ready ${clockTime(t.ready_at, ctx.timeZone)}` : ''}
                          {t.served_at ? ` · served ${clockTime(t.served_at, ctx.timeZone)}` : ''}
                        </div>
                      </div>
                      <span className="d-inline">
                        <Badge tone={ticketTone(t.status)}>{t.status.replace('_', ' ')}</Badge>
                        {canServeHere && t.status === 'served' && live && (
                          <CorrectServeButton
                            servedAt={t.served_at}
                            undoWindow={undoWindow}
                            canCorrect={s.can('serve.correct')}
                            disabled={serveActions.pending[t.id]?.state === 'sending'}
                            label={stationName(t.production_station_id)}
                            testIdPrefix="orders"
                            onPress={(nowMs) =>
                              correctServe(t, stationName(t.production_station_id), nowMs)
                            }
                          />
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            )}
            <section className="d-stack d-stack--tight">
              <SectionHead level={3} title="Timeline" />
              <div className="d-list" data-testid="order-events">
                {sel.events.map((e) => {
                  const payload = (e.payload ?? {}) as Record<string, unknown>;
                  const reason = typeof payload.reason === 'string' ? payload.reason : null;
                  const label = EVENT_LABEL[e.event_type];
                  return (
                    <div className="d-row" key={e.id}>
                      <div className="d-row__main">
                        <div className="d-row__title orders-event">
                          {label && <span>{label}</span>}{' '}
                          <span className="d-mono orders-event__type">{e.event_type}</span>
                        </div>
                        <div className="d-row__meta">
                          {formatInTimeZone(e.occurred_at, ctx.timeZone)}
                          {e.station_id ? ` · ${stationName(e.station_id)}` : ''}
                          {reason ? ` · “${reason}”` : ''}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
            {(offerRefund || offerRefundOutside || offerCancel || offerVoid) && (
              <div className="d-sheet__actions">
                {offerRefund && (
                  <Button
                    variant="ghost"
                    disabled={!online}
                    onClick={() => setRefunding(true)}
                    data-testid="orders-refund"
                  >
                    Refund
                  </Button>
                )}
                {offerRefundOutside && (
                  <Button
                    variant="ghost"
                    disabled={!online}
                    onClick={() => setRefundingOutside(true)}
                    data-testid="orders-refund-outside"
                  >
                    Refund by GCash or transfer
                  </Button>
                )}
                {offerCancel && (
                  <Button
                    variant="ghost"
                    disabled={!online}
                    onClick={cancel}
                    data-testid="orders-cancel"
                  >
                    Cancel order
                  </Button>
                )}
                {offerVoid && (
                  <Button
                    variant="danger"
                    disabled={!online}
                    onClick={voidOrder}
                    data-testid="orders-void"
                  >
                    Void order
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </Sheet>
      <RefundSheet
        order={refunding && sel ? sel.order : null}
        items={sel?.items ?? []}
        methods={methods.data ?? []}
        currency={ctx.currency}
        busy={busy}
        onClose={() => setRefunding(false)}
        onRefund={(input) => refund(input)}
      />
      <OutsideShiftRefundSheet
        order={refundingOutside && sel ? sel.order : null}
        refundableMinor={refundable}
        methods={methods.data ?? []}
        currency={ctx.currency}
        onClose={() => setRefundingOutside(false)}
      />
      <SlipSheet receipt={slip} currency={ctx.currency} onClose={() => setSlip(null)} />
      {giveBack && giveBack.reversalIds.length > 0 && (
        <GiveBackSheet
          orderId={giveBack.orderId}
          reversalIds={giveBack.reversalIds}
          currency={ctx.currency}
          run={run}
          onClose={() => setGiveBack(null)}
        />
      )}
      <ReasonSheet request={reason} onClose={() => setReason(null)} />
      {cashNow.sheet}
      <ApprovalSheet request={approval} onClose={() => setApproval(null)} />
    </div>
  );
}
